// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { components } from "@/api/schema";

export type StoreImportFieldKey =
  | "chainName"
  | "storeName"
  | "storeCode"
  | "legibleName"
  | "timeZone"
  | "goLiveDate"
  // Address fields — mapped only when the optional Address section is enabled; patched via edit.
  | "address"
  | "city"
  | "state"
  | "zipCode"
  | "country"
  | "region"
  // Store and camera hours — mapped only when the optional Hours section is enabled; patched via
  // edit, since the create endpoint defaults them server-side and accepts no override.
  | "openTime"
  | "closeTime"
  | "camerasOnTime"
  | "camerasOffTime";

/** How a field's value is sourced: a sheet column, a fixed constant, or computed (derived). */
export type FieldSourceMode = "column" | "constant" | "computed";

export interface FieldSource {
  mode: FieldSourceMode;
  /** Selected column index for `column` mode (null = not yet chosen). */
  column: number | null;
  /** Fixed value for `constant` mode. */
  value: string;
}

export type FieldSources = Record<StoreImportFieldKey, FieldSource>;

/** Constant-mode editor style — mirrors the single create-store dialog's controls. */
export type ConstantInput = "text" | "chain" | "timezone" | "country" | "time";

export interface StoreImportField {
  key: StoreImportFieldKey;
  required: boolean;
  /** Normalised header tokens that auto-map to this field (besides the key itself). */
  aliases: string[];
  constantInput: ConstantInput;
  /** Offers `computed` mode. storeName → snake(chain)_snake(code); legibleName → column + " (code)". */
  computable?: boolean;
  /** Computed mode derives from a picked base column (legibleName) rather than from other fields. */
  computedFromColumn?: boolean;
  /** Unmatched columns default to `computed` mode instead of an empty column source (storeName). */
  defaultComputed?: boolean;
}

/** Target create-store fields the sheet columns map onto, in display order. */
export const STORE_IMPORT_FIELDS: StoreImportField[] = [
  { key: "chainName", required: true, aliases: ["chain"], constantInput: "chain" },
  {
    key: "storeName",
    required: true,
    aliases: ["store", "name"],
    constantInput: "text",
    computable: true,
    defaultComputed: true,
  },
  { key: "storeCode", required: true, aliases: ["code"], constantInput: "text" },
  {
    key: "legibleName",
    required: true,
    aliases: ["legible", "displayname", "fullname", "label"],
    constantInput: "text",
    computable: true,
    computedFromColumn: true,
  },
  { key: "timeZone", required: true, aliases: ["timezone", "tz"], constantInput: "timezone" },
  { key: "goLiveDate", required: false, aliases: ["golive", "launchdate", "date"], constantInput: "text" },
];

/**
 * Optional store-hours fields (only mapped when the Hours section is enabled; applied via edit).
 *
 * These cannot ride along on the create call: `POST /v1/stores` accepts only the store skeleton and
 * defaults hours to 07:00–22:00 server-side.
 */
export const HOURS_IMPORT_FIELDS: StoreImportField[] = [
  {
    key: "openTime",
    required: false,
    aliases: ["open", "opens", "opening", "openinghours", "openhour", "storeopen", "storeopentime"],
    constantInput: "time",
  },
  {
    key: "closeTime",
    required: false,
    aliases: ["close", "closes", "closing", "closinghours", "closehour", "storeclose", "storeclosetime"],
    constantInput: "time",
  },
];

/**
 * Optional camera on/off times — same section and same follow-up patch as the store hours, since
 * both live in the edit request's `operations` block. Create defaults these server-side too.
 */
export const CAMERA_IMPORT_FIELDS: StoreImportField[] = [
  {
    key: "camerasOnTime",
    required: false,
    aliases: ["cameraon", "camerason", "cameraontime", "camerasontime", "camerastart", "camerasstart"],
    constantInput: "time",
  },
  {
    key: "camerasOffTime",
    required: false,
    aliases: ["cameraoff", "camerasoff", "cameraofftime", "camerasofftime", "cameraend", "camerasend"],
    constantInput: "time",
  },
];

/** The two open/close pairs the API validates as ordered ranges, for per-row preview checks. */
export const TIME_RANGE_PAIRS: { from: StoreImportFieldKey; to: StoreImportFieldKey; errorKey: string }[] = [
  { from: "openTime", to: "closeTime", errorKey: "stores.bulkCreate.errors.timeOrder" },
  { from: "camerasOnTime", to: "camerasOffTime", errorKey: "stores.bulkCreate.errors.cameraTimeOrder" },
];

/** Optional address fields (only mapped when the Address section is enabled; applied via edit). */
export const ADDRESS_IMPORT_FIELDS: StoreImportField[] = [
  { key: "address", required: false, aliases: ["street", "addressline", "address1"], constantInput: "text" },
  { key: "city", required: false, aliases: ["town"], constantInput: "text" },
  { key: "state", required: false, aliases: ["province", "county"], constantInput: "text" },
  { key: "zipCode", required: false, aliases: ["zip", "postcode", "postalcode", "postal"], constantInput: "text" },
  { key: "country", required: false, aliases: [], constantInput: "country" },
  { key: "region", required: false, aliases: [], constantInput: "text" },
];

/** Field → i18n label key (reuse the single create-store form labels). Shared by the mapping
 *  controls and the preview table so both name columns identically. */
export const STORE_FIELD_LABEL_KEY: Record<StoreImportFieldKey, string> = {
  chainName: "stores.form.chainName",
  storeName: "stores.form.storeName",
  storeCode: "stores.form.storeCode",
  legibleName: "stores.form.legibleName",
  timeZone: "stores.form.timeZone",
  goLiveDate: "stores.form.goLiveDate",
  address: "stores.form.address",
  city: "stores.form.city",
  state: "stores.form.state",
  zipCode: "stores.form.zipCode",
  country: "stores.form.country",
  region: "stores.form.region",
  openTime: "stores.info.fields.openTime",
  closeTime: "stores.info.fields.closeTime",
  camerasOnTime: "stores.info.fields.cameraOnTime",
  camerasOffTime: "stores.info.fields.cameraOffTime",
};

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

/** snake_case a label the way the single create-store dialog derives store names/keys. */
export function toSnakeCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/**
 * Initial per-field sources for a set of headers: auto-map columns by normalised name/alias (first
 * match wins, never reusing a column). Fields with no matching column default to `computed` when
 * they support it (store name), otherwise to an empty `column` source for the user to fill in.
 */
export function initialFieldSources(headers: string[]): FieldSources {
  const normalizedHeaders = headers.map(normalize);
  const used = new Set<number>();
  const sources = {} as FieldSources;
  for (const field of [
    ...STORE_IMPORT_FIELDS,
    ...ADDRESS_IMPORT_FIELDS,
    ...HOURS_IMPORT_FIELDS,
    ...CAMERA_IMPORT_FIELDS,
  ]) {
    const candidates = new Set([normalize(field.key), ...field.aliases.map(normalize)]);
    const index = normalizedHeaders.findIndex((header, i) => !used.has(i) && header !== "" && candidates.has(header));
    if (index !== -1) {
      used.add(index);
      sources[field.key] = { mode: "column", column: index, value: "" };
    } else {
      sources[field.key] = { mode: field.defaultComputed ? "computed" : "column", column: null, value: "" };
    }
  }
  return sources;
}

/**
 * Resolve a field's value for a given row from its source. Computed modes:
 * - storeName: `snake(chain)_snake(storeCode)` (matches the single create dialog).
 * - legibleName: the picked base column value with the store code appended, e.g. `Acme Downtown (001)`.
 */
export function resolveField(row: string[], sources: FieldSources, key: StoreImportFieldKey): string {
  const source = sources[key];
  if (source.mode === "constant") return source.value.trim();
  if (source.mode === "column") return source.column != null ? (row[source.column] ?? "").trim() : "";
  // Computed:
  if (key === "legibleName") {
    const base = source.column != null ? (row[source.column] ?? "").trim() : "";
    if (!base) return "";
    const code = resolveField(row, sources, "storeCode");
    return code ? `${base} (${code})` : base;
  }
  const chain = toSnakeCase(resolveField(row, sources, "chainName"));
  const code = toSnakeCase(resolveField(row, sources, "storeCode"));
  return chain && code ? `${chain}_${code}` : "";
}

/**
 * Normalise a spreadsheet time cell to the `HH:mm` the API expects, or null when unparseable.
 *
 * The sheet parser reads cells with `raw: false`, so an Excel time arrives as whatever text the
 * cell displayed — `7:00`, `07:00:00`, `7:00 AM`, `10:30 PM`. Seconds are dropped and 12-hour
 * values are converted; anything else (a stray date, free text) returns null so the row can be
 * flagged before the import runs rather than 422-ing mid-flight.
 */
export function normalizeTime(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;
  const match = /^(\d{1,2})[:.](\d{2})(?:[:.](\d{2}))?\s*(am|pm)?$/i.exec(raw);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[4]?.toLowerCase();
  if (minutes > 59) return null;
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === "am") hours = hours === 12 ? 0 : hours;
    else hours = hours === 12 ? 12 : hours + 12;
  } else if (hours > 23) {
    return null;
  }
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** A row's mapped store and camera times, normalised. `null` = absent or unparseable. */
export interface OperationsTimes {
  openTime: string | null;
  closeTime: string | null;
  camerasOnTime: string | null;
  camerasOffTime: string | null;
}

/** Resolve a row's mapped store-hours and camera times, normalised to `HH:mm`. */
export function resolveOperationsTimes(row: string[], sources: FieldSources): OperationsTimes {
  return {
    openTime: normalizeTime(resolveField(row, sources, "openTime")),
    closeTime: normalizeTime(resolveField(row, sources, "closeTime")),
    camerasOnTime: normalizeTime(resolveField(row, sources, "camerasOnTime")),
    camerasOffTime: normalizeTime(resolveField(row, sources, "camerasOffTime")),
  };
}

/** Whether a row has any usable time at all — i.e. whether the follow-up patch is needed. */
export function hasOperationsTimes(times: OperationsTimes): boolean {
  return Object.values(times).some((value) => value !== null);
}

/** Whether a field's source is usable at config time (per-row emptiness is checked separately). */
export function isFieldConfigured(source: FieldSource): boolean {
  if (source.mode === "column") return source.column != null;
  if (source.mode === "constant") return source.value.trim() !== "";
  return true; // computed
}

/** Build a StoresCreateRequest body from a row + sources. `isActive` controls the created state. */
export function buildStoreCreateBody(
  row: string[],
  sources: FieldSources,
  isActive = true,
): components["schemas"]["StoresCreateRequest"] {
  return {
    chainName: resolveField(row, sources, "chainName"),
    storeName: resolveField(row, sources, "storeName"),
    timeZone: resolveField(row, sources, "timeZone"),
    info: {
      storeCode: resolveField(row, sources, "storeCode"),
      legibleName: resolveField(row, sources, "legibleName"),
      goLiveDate: resolveField(row, sources, "goLiveDate") || undefined,
      isActive,
    },
  };
}

/** Resolve the address block for a row (empty fields omitted). */
export function buildStoreAddress(
  row: string[],
  sources: FieldSources,
): components["schemas"]["StoresEditAddressRequest"] {
  const value = (key: StoreImportFieldKey) => resolveField(row, sources, key) || undefined;
  return {
    address: value("address"),
    city: value("city"),
    state: value("state"),
    zipCode: value("zipCode"),
    country: value("country"),
    region: value("region"),
  };
}

/**
 * The slice of a store read-back the operations section needs. Deliberately narrower than
 * `StoresDetailsResponse`: taking the whole response would drag in its `schedule` nesting, whose
 * tuple types don't match between the schema alias and what the fetch client infers.
 */
export interface StoreOperationsSnapshot {
  details?: components["schemas"]["StoresDetailsDetailsResponse"];
  cameras?: components["schemas"]["StoresDetailsCamerasResponse"];
  impactSettings?: components["schemas"]["StoresDetailsImpactSettingsResponse"];
  matchSettings?: components["schemas"]["StoresDetailsMatchSettingsResponse"];
}

/**
 * Build the `operations` section that applies a row's store hours.
 *
 * The API has no hours-only patch: `StoresEditOperationsSection` requires `details`, `cameras`,
 * `impactSettings` and `matchSettings` together, and the route writes all four unconditionally
 * (impact settings even get a new history record). So the three we aren't changing are echoed back
 * verbatim from the store we just created — the backend's change detection makes those a no-op —
 * and only `details` carries new values. Fabricating defaults instead would silently overwrite
 * whatever `copyFrom` brought across.
 *
 * Returns null when the row has no usable times — or when the store read-back is missing a section
 * we'd have to echo, since guessing at those is exactly what this function exists to avoid. The
 * caller distinguishes the two cases via `resolveOperationsTimes`.
 */
export function buildStoreOperationsSection(
  row: string[],
  sources: FieldSources,
  current: StoreOperationsSnapshot,
): components["schemas"]["StoresEditOperationsSection"] | null {
  const times = resolveOperationsTimes(row, sources);
  if (!hasOperationsTimes(times)) return null;
  const details = current.details;
  const impact = current.impactSettings;
  const match = current.matchSettings;
  const cameras = current.cameras;
  if (!details || !cameras || !impact) return null;
  return {
    details: {
      timeZone: details.timeZone,
      // A half-mapped row keeps the store's existing value for the other end of the range.
      openTime: times.openTime ?? details.openTime.slice(0, 5),
      closeTime: times.closeTime ?? details.closeTime.slice(0, 5),
    },
    cameras: {
      deviceType: cameras.deviceType,
      onTime: times.camerasOnTime ?? cameras.onTime.slice(0, 5),
      offTime: times.camerasOffTime ?? cameras.offTime.slice(0, 5),
    },
    // Nullable fields come back as null; send undefined so they're omitted rather than cleared.
    impactSettings: {
      minWorkable: impact.minWorkable,
      sortingLogic: impact.sortingLogic,
      priorityLogic: impact.priorityLogic,
      isPromoOnTop: impact.isPromoOnTop,
      isAssociatesCanSort: impact.isAssociatesCanSort,
      isShowDsdItems: impact.isShowDsdItems,
      isShowNonWorkable: impact.isShowNonWorkable,
      maxInventoryLevelAge: impact.maxInventoryLevelAge ?? undefined,
      filterUpcSources: impact.filterUpcSources ?? undefined,
      filterUpcSourcesForHooks: impact.filterUpcSourcesForHooks ?? undefined,
    },
    matchSettings: {
      isHidePog: match?.isHidePog ?? undefined,
      isDisableIngress: match?.isDisableIngress ?? undefined,
      isExcludeFromQueue: match?.isExcludeFromQueue ?? undefined,
      cooldownHours: match?.cooldownHours ?? undefined,
      isDiscrepDisableIngress: match?.isDiscrepDisableIngress ?? undefined,
    },
  };
}

/**
 * Build a StoresEditRequest that patches a created store's address and/or hours.
 *
 * Both sections are optional and independent: `info` carries the address (and re-sends the core
 * profile fields the section requires), `operations` carries the hours. Passing neither yields an
 * empty body, which the API reports as no-change.
 */
export function buildStoreEditBody(
  row: string[],
  sources: FieldSources,
  isActive = true,
  options: { address?: boolean; operations?: components["schemas"]["StoresEditOperationsSection"] | null } = {
    address: true,
  },
): components["schemas"]["StoresEditRequest"] {
  const create = buildStoreCreateBody(row, sources, isActive);
  const body: components["schemas"]["StoresEditRequest"] = {};
  if (options.address) {
    body.info = {
      storeCode: create.info.storeCode,
      legibleName: create.info.legibleName,
      goLiveDate: create.info.goLiveDate,
      isActive: create.info.isActive,
      address: buildStoreAddress(row, sources),
    };
  }
  if (options.operations) body.operations = options.operations;
  return body;
}
